import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/** Polls /api/notifications every 30s. Shows an unread badge and a popover panel
 *  with mark-as-read + clear-all actions. */
const NotificationBell = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const seenIdsRef = useRef(new Set());  // ids we've already toasted (or seen), so a dismiss can't resurface them
  const hasInitedRef = useRef(false);

  const fetchNotes = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=20");

      // Pop a toast for any notifications that arrived since the last poll.
      // Skip on initial mount so we don't dump the whole inbox at the user.
      if (hasInitedRef.current) {
        const fresh = data.notifications.filter(
          (n) => !n.is_read && !seenIdsRef.current.has(n.notification_id)
        );
        for (const n of fresh.slice(0, 3)) {
          toast(n.title, {
            description: n.message,
            action: n.action_link
              ? { label: "Open", onClick: () => { window.location.href = n.action_link; } }
              : undefined,
          });
        }
      }
      hasInitedRef.current = true;
      for (const n of data.notifications) seenIdsRef.current.add(n.notification_id);

      setNotes(data.notifications);
      setUnread(data.unread_count);
    } catch {
      // silent — bell is a non-critical surface
    }
  }, []);

  useEffect(() => {
    fetchNotes();
    // Poll every 60s and skip when the tab is hidden — reduces backend load and
    // saves Atlas connection pool slots in resource-constrained deploys.
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      fetchNotes();
    };
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [fetchNotes]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    fetchNotes();
  };

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    fetchNotes();
  };

  const dismiss = async (id) => {
    await api.delete(`/notifications/${id}`);
    fetchNotes();
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 hover:bg-gray-100 rounded-md transition"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4 text-gray-500" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-rose-600 text-white text-[9px] font-bold rounded-full grid place-items-center"
            data-testid="notification-unread-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] bg-[#0D1117] border border-[#374151] shadow-2xl z-50" data-testid="notification-panel">
          <div className="border-b border-[#1F2937] px-4 py-3 flex items-center gap-2">
            <Bell className="w-3.5 h-3.5 text-gray-500" />
            <div className="font-display font-bold tracking-tight text-sm">Notifications</div>
            <div className="text-[10px] text-gray-500 font-mono ml-1">({unread} unread)</div>
            {unread > 0 && (
              <button onClick={markAll} className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#002FA7] hover:underline" data-testid="mark-all-read-btn">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[460px] overflow-y-auto">
            {notes.length === 0 ? (
              <div className="p-8 text-center text-xs text-gray-500">You're all caught up.</div>
            ) : (
              <div className="divide-y divide-[#1F2937]">
                {notes.map((n) => (
                  <div key={n.notification_id} className={`p-3 hover:bg-[#030712] group ${!n.is_read ? "bg-[#0D1117]" : ""}`} data-testid={`notification-${n.notification_id}`}>
                    <div className="flex items-start gap-2">
                      {!n.is_read && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#002FA7] mt-1.5 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-white truncate">{n.title}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{n.message}</div>
                        <div className="flex items-center gap-2 mt-2 text-[10px]">
                          {n.action_link && (
                            <button
                              onClick={() => {
                                if (!n.is_read) markRead(n.notification_id);
                                setOpen(false);
                                setTimeout(() => navigate(n.action_link), 0);
                              }}
                              className="text-[#002FA7] hover:underline flex items-center gap-1 font-medium"
                              data-testid={`notif-action-${n.notification_id}`}
                            >
                              Open <ExternalLink className="w-2.5 h-2.5" />
                            </button>
                          )}
                          {!n.is_read && (
                            <button onClick={() => markRead(n.notification_id)} className="text-gray-500 hover:text-white flex items-center gap-1" data-testid={`mark-read-${n.notification_id}`}>
                              <Check className="w-2.5 h-2.5" /> Mark read
                            </button>
                          )}
                        </div>
                      </div>
                      <button onClick={() => dismiss(n.notification_id)} className="text-gray-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition" data-testid={`dismiss-${n.notification_id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
